import { useEffect, useRef, useState, useCallback, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Check, Wrench, Calculator, ShieldAlert, ExternalLink } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';

// Shared within this browser bundle: the bell and center must not race each other.
const ownerMutations = new Map();
import { displayDate, localNotificationPath, notificationValue } from '@/lib/service-view-guards.mjs';
import { useNotificationFeed } from '@/lib/useNotificationFeed';
const SRC = {
  service: { label: 'Servicios', Icon: Wrench, cls: 'text-sky-300' },
  accounting: { label: 'Contabilidad', Icon: Calculator, cls: 'text-emerald-300' },
  security: { label: 'Seguridad', Icon: ShieldAlert, cls: 'text-amber-300' },
};
export const fmtDT = displayDate;
export function NotificationRow({ n, onOpen, onRead, compact = true, busy = false }) {
  const m = SRC[n.source];
  if (!m) return null;
  return <div className={`flex items-start gap-2 px-3 py-2.5 border-b border-neutral-800 ${n.read ? 'opacity-70' : ''}`} data-testid={`notif-${n.source}-${n.id}`}>
    <button type="button" disabled={busy} onClick={() => onOpen(n)} className="flex-1 text-left min-w-0 min-h-11" data-testid={`notif-open-${n.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs"><span className={m.cls}><m.Icon size={11} aria-hidden="true"/>{m.label}</span><span>{fmtDT(n.created_at)}</span><span className="sr-only">{n.read ? 'Leída' : 'Sin leer'}</span></div>
      <div className="text-sm font-semibold mt-1 break-words">{n.title}</div>{n.description && <div className={`text-xs text-neutral-300 ${compact ? 'truncate' : 'break-words'}`}>{n.description}</div>}
    </button>
    {!n.read && <button type="button" disabled={busy} onClick={() => onRead(n)} aria-label={`Marcar como leída: ${n.title}`} className="text-neutral-300 min-w-11 min-h-11 flex items-center justify-center" data-testid={`notif-read-${n.id}`}><Check size={16} aria-hidden="true"/></button>}
  </div>;
}
export function useNotificationActions(reload) {
  const nav = useNavigate(), lock = useRef(false), active = useRef(false);
  const { user } = useAuth();
  const owner = user?.id ? `${user.id}:${user.role}` : null;
  const [busy, setBusy] = useState(false), [message, setMessage] = useState('');
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  const refresh = useCallback(() => { try { Promise.resolve(reload?.()).catch(() => {}); } catch { /* feed owns read errors */ } }, [reload]);
  const execute = useCallback(async (kind, input) => {
    if (!active.current || lock.current || !owner) return;
    if (ownerMutations.has(owner)) { setMessage('Hay una acción de lectura en curso. Espera a que termine.'); return; }
    let n;
    if (kind !== 'all') {
      try { n = notificationValue(input); } catch { setMessage('No pudimos validar esta notificación. Actualiza la lista.'); return; }
    }
    const operation = {}; ownerMutations.set(owner, operation);
    lock.current = true; setBusy(true); setMessage('');
    let readFailed = false;
    try {
      if (kind === 'all') {
        const { data } = await api.post('/notifications/center/read-all', {}, { timeout: 15000 });
        if (!Number.isSafeInteger(data?.updated) || data.updated < 0) throw Error('invalid_receipt');
        if (active.current) setMessage(`${data.updated} notificaciones marcadas como leídas.`);
      } else if (!n.read) {
        // Existing client contract: this endpoint acknowledges via HTTP success.
        await api.post(`/notifications/center/${n.source}/${encodeURIComponent(n.id)}/read`, {}, { timeout: 15000 });
      }
    } catch { readFailed = true; if (active.current) setMessage('No pudimos confirmar la marca de lectura. Actualiza la lista para comprobar el estado.'); }
    finally {
      lock.current = false;
      if (ownerMutations.get(owner) === operation) ownerMutations.delete(owner);
      if (active.current) {
        setBusy(false); refresh();
        if (kind === 'open') {
          if (readFailed) toast.error('Abrimos el destino, pero no pudimos confirmar la marca de lectura.');
          nav(localNotificationPath(n.link), { state: readFailed ? { notificationReadPending: true } : undefined });
        }
      }
    }
  }, [nav, refresh, owner]);
  return { busy, message, markRead: useCallback(n => execute('read', n), [execute]),
    open: useCallback(n => execute('open', n), [execute]), markAll: useCallback(() => execute('all'), [execute]) };
}
export function NotificationFeedState({ feed, emptyLabel = 'Sin notificaciones' }) {
  return <>
    {feed.loading && <p role="status" className="p-4 text-sm">Cargando notificaciones…</p>}
    {feed.paused && <p role="status" className="p-4 text-sm">Actualización en pausa. Recupera la conexión para continuar.</p>}
    {feed.error && <div role="alert" className="p-4 text-sm">{feed.error === 'unavailable' ? 'Notificaciones no disponibles. Revisa tu acceso.' : 'No pudimos actualizar las notificaciones.'}
      {feed.error !== 'unavailable' && <button type="button" disabled={feed.loading} onClick={feed.reload} className="block underline min-h-11">Reintentar notificaciones</button>}</div>}
    {!feed.loading && !feed.error && !feed.paused && feed.data?.items.length === 0 && <p className="p-6 text-center text-sm">{emptyLabel}</p>}
  </>;
}
export function NotificationsBell() {
  const { user } = useAuth();
  return user?.id ? <BellSession key={`${user.id}:${user.role}`} /> : null;
}
function BellSession() {
  const feed = useNotificationFeed({ poll: 30000 });
  const unread = feed.data?.counts.unread;
  const [open, setOpen] = useState(false), ref = useRef(null), button = useRef(null), region = useRef(null);
  const id = useId(), nav = useNavigate(), acts = useNotificationActions(feed.reload);
  useEffect(() => {
    if (!open) return;
    region.current?.focus();
    const close = event => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
    const escape = event => { if (event.key === 'Escape' && ref.current?.contains(event.target)) { event.preventDefault(); setOpen(false); button.current?.focus(); } };
    document.addEventListener('pointerdown', close); document.addEventListener('focusin', close); document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('focusin', close); document.removeEventListener('keydown', escape); };
  }, [open]);
  return <div className="relative" ref={ref}>
    <button type="button" ref={button} onClick={() => setOpen(value => !value)} className="relative min-w-11 min-h-11 p-2 rounded" data-testid="btn-notifications-bell" aria-label={`Notificaciones${unread == null ? '' : `: ${unread} sin leer`}`} aria-expanded={open} aria-controls={id}>
      <Bell size={18} aria-hidden="true"/>{unread > 0 && <span aria-hidden="true" data-testid="notifications-badge" className="absolute top-0 right-0 rounded-full bg-[#E63946] text-white px-1 text-xs">{unread > 99 ? '99+' : unread}</span>}
    </button>
    {open && <section id={id} ref={region} tabIndex={-1} aria-label="Notificaciones recientes" className="absolute right-0 mt-2 w-[380px] max-w-[92vw] bg-[#0D0D0D] border border-neutral-700 rounded-xl shadow-2xl z-50 text-neutral-100" data-testid="notifications-dropdown" aria-busy={feed.loading || acts.busy}>
      <div className="flex flex-wrap items-center justify-between px-3 py-2 border-b border-neutral-800"><h2>Notificaciones</h2>{unread > 0 && <button type="button" disabled={acts.busy || !!feed.error || feed.paused} onClick={acts.markAll} className="text-xs min-h-11" data-testid="btn-notifications-mark-all"><CheckCheck size={12}/> Marcar todas como leídas</button>}</div>
      {acts.message && <p role="status" className="p-3 text-sm">{acts.message}</p>}
      <div className="max-h-[60vh] overflow-y-auto"><NotificationFeedState feed={feed} emptyLabel="Sin notificaciones recientes"/>{feed.data?.items.map(n => <NotificationRow key={`${n.source}-${n.id}`} n={n} busy={acts.busy || !!feed.error || feed.paused} onOpen={acts.open} onRead={acts.markRead}/>)}</div>
      <button type="button" onClick={() => { setOpen(false); nav('/admin/notifications'); }} className="w-full p-3 text-sm text-[#E63946] border-t border-neutral-800" data-testid="btn-notifications-view-all"><ExternalLink size={12}/> Ver todas las notificaciones</button>
    </section>}
  </div>;
}
